import React from "react";

import { stepCompleted } from "EmployeeApplication/actions";
import { getDependents, getEmployeeApplication, getEmployeeForm, useUpdateEmployeePolicyMutation } from "EmployeeApplication/api";
import { getEnrollmentSegment } from "EmployeeApplication/selectors";
import AgeEnrollmentModal, { AgeEnrollmentFormValueProps } from "EmployeeCoverage/Components/EnrollmentModal/AgeEnrollmentModal";
import SegmentEnrollmentModal from "EmployeeCoverage/Components/EnrollmentModal/SegmentEnrollmentModal";
import { useDispatch, useSelector } from "react-redux";
import AnalyticsTracking from "Utils/AnalyticsTracking";
import { formatNameFL } from "Utils/formatName";
import { EmployeeForm, EmployeePolicy, PlanCost, EmployeeDependent, MedicalPlan } from "Utils/types";

import Button, { ButtonProps } from "Cobalt/Button";

import { useMedicalContext } from "./medical.context";

interface EntitiesToAgeFormInitialValuesProps {
  employeeForm: EmployeeForm;
  employeePolicy: EmployeePolicy;
  dependents: Array<EmployeeDependent>;
}
const entitiesToAgeFormInitialValues = ({ employeeForm, employeePolicy, dependents }: EntitiesToAgeFormInitialValuesProps): AgeEnrollmentFormValueProps => {
  const { dependentEnrollments } = employeePolicy;
  const dependentsFormatted = dependents.map((dependent) => {
    const dependentEnrollment = dependentEnrollments.find(({ employeeDependentID }) => dependent.id === employeeDependentID);
    let enrolling = null;
    if (dependentEnrollment?.medical === true) {
      enrolling = "Yes";
    } else if (dependentEnrollment?.medical === false) {
      enrolling = "No";
    }

    return {
      dependentID: dependent.id,
      formattedName: formatNameFL(dependent),
      dependentType: dependent.dependentType,
      enrolling,
    };
  });

  return {
    employeeEnrolling: employeePolicy.isEnrolling,
    employeeFormattedName: formatNameFL(employeeForm),
    dependents: dependentsFormatted,
  };
};

export const MedicalEnrollmentModals = () => {
  const { plan, planCost, isShowing, hideEnrollmentModal } = useMedicalContext();
  const [updateEmployeePolicy] = useUpdateEmployeePolicyMutation();
  const employeePolicy = useSelector(getEmployeeApplication);
  const employeeForm = useSelector(getEmployeeForm);
  const employeeDependents = useSelector(getDependents) || [];
  const dispatch = useDispatch();
  const closeModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "close_enroll_or_decline_modal",
    });
    hideEnrollmentModal();
  };

  const onSubmitAgeEnrollment = async (values) => {
    const isEnrollingMedical = values.employeeEnrolling;
    const dependentsEnrollingMedical = {};
    values.dependents.forEach((dependent) => {
      dependentsEnrollingMedical[dependent.dependentID] = isEnrollingMedical === "Yes" && dependent.enrolling === "Yes";
    });
    const body = {
      id: employeePolicy.id,
      isEnrolling: values.employeeEnrolling,
      enrolledPlanID: isEnrollingMedical === "Yes" ? plan.id : null,
      dependentsEnrolling: dependentsEnrollingMedical,
      termLifeCoverageAmount: values.selectedAmount,
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseMedicalPlan"));
    }
  };

  if (planCost?.pricingType === "age") {
    return (
      <AgeEnrollmentModal
        plan={plan}
        employeePolicyID={employeePolicy.id}
        initialValues={entitiesToAgeFormInitialValues({
          employeeForm,
          employeePolicy,
          dependents: employeeDependents,
        })}
        title="Enroll In Medical Coverage"
        onSubmit={onSubmitAgeEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  const currentSegment = getEnrollmentSegment(employeePolicy, employeeDependents, "medical");

  const onSubmitSegmentEnrollment = async ({ selectedSegment }) => {
    const spousesOrDomesticPartners = employeeDependents.filter((d) => d.dependentType === "spouse" || d.dependentType === "domestic_partner");
    const children = employeeDependents.filter((d) => d.dependentType === "child");
    let enrolledDependents = [];
    if (selectedSegment === "ee_spouse") {
      enrolledDependents = spousesOrDomesticPartners.map(({ id }) => id);
    } else if (selectedSegment === "ee_children") {
      enrolledDependents = children.map(({ id }) => id);
    } else if (selectedSegment === "ee_family") {
      enrolledDependents = [...spousesOrDomesticPartners, ...children].map(({ id }) => id);
    }
    const body = {
      id: employeePolicy.id,
      isEnrolling: selectedSegment === "none" ? "No" : "Yes",
      enrolledPlanID: selectedSegment === "none" ? null : plan.id,
      dependentsEnrolling: employeeDependents.reduce((acc, { id }) => {
        acc[id] = enrolledDependents.includes(id);
        return acc;
      }, {}),
    };
    const { data } = await updateEmployeePolicy(body);
    if (data) {
      dispatch(stepCompleted("chooseMedicalPlan"));
    }
  };

  if (planCost?.pricingType === "segment") {
    return (
      <SegmentEnrollmentModal
        plan={plan}
        planCost={planCost}
        title="Enroll In Medical Coverage"
        initialValues={{ selectedSegment: currentSegment }}
        onSubmit={onSubmitSegmentEnrollment}
        onHide={closeModal}
        show={isShowing}
      />
    );
  }

  return null;
};

interface MedicalEnrollOrDeclineButtonProps extends ButtonProps {
  plan: MedicalPlan;
  planCost: PlanCost;
}

export const MedicalEnrollOrDeclineButton = ({ plan, planCost }: MedicalEnrollOrDeclineButtonProps) => {
  const { openEnrollmentModal } = useMedicalContext();
  const openModal = () => {
    AnalyticsTracking.trackEvent("cta_clicked", {
      location: "employee_application_choose_coverage_page",
      label: "open_enroll_or_decline_modal",
    });
    openEnrollmentModal({ plan, planCost });
  };

  return <Button onClick={openModal}>Enroll or Decline</Button>;
};
